import React, { useState, useEffect } from "react";
import { useMutation, gql } from "@apollo/client";
import {
  OutlinedInput,
  Typography,
  Button,
  Box,
  Paper,
  Grid,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

// GraphQL mutation for changing the password
const CHANGE_PASSWORD = gql`
  mutation ($email: String!, $password: String!, $newPassword: String!) {
    changePassword(
      email: $email
      password: $password
      newPassword: $newPassword
    ) {
      email
    }
  }
`;

function ChangePassword(props) {
  // State variables to hold input values, errors, loading, and success state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();

  // Initialize the mutation function from Apollo Client
  const [changePasswordMutation] = useMutation(CHANGE_PASSWORD);

  // Redirect if the user's current password is null (security measure)
  useEffect(() => {
    if (props?.userData?.user?.password === null) {
      window.location.href = "/biz";
    }
  }, [props]);

  // After a successful password change, navigate to the login page
  useEffect(() => {
    if (success) {
      navigate("/login");
    }
  }, [success, navigate]);

  // Handler for form submission
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError([]); // Clear previous errors

    const errors = [];

    // Validate that the new passwords match
    if (newPassword !== confirmPassword) {
      errors.push("Passwords don't match");
    }
    // Validate that the new password meets the length requirement
    if (newPassword.length < 6) {
      errors.push("Password is too short (minimum 6 characters)");
    }

    // If there are any validation errors, update the state and abort submission
    if (errors.length > 0) {
      setError(errors);
      return;
    }

    // Set loading state and perform the mutation call
    setLoading(true);
    try {
      await changePasswordMutation({
        variables: {
          email: props?.userData?.user?.email,
          password: props?.userData?.user?.password,
          newPassword,
        },
      });
      setLoading(false);
      setSuccess(true);
    } catch (err) {
      setLoading(false);
      // If there are GraphQL errors, extract and display them
      const graphqlErrors = err.graphQLErrors
        ? err.graphQLErrors.map((e) => e.message)
        : [err.message];
      setError(graphqlErrors);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      marginTop="50px"
      flexDirection="column"
    >
      <Paper
        elevation={3}
        style={{
          padding: "30px",
          maxWidth: "400px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Typography variant="h5" align="center" gutterBottom>
          Change Password
        </Typography>
        <form onSubmit={handleChangePassword}>
          <Grid container spacing={2}>
            {/* Disabled email field */}
            <Grid item xs={12}>
              <OutlinedInput
                fullWidth
                placeholder="Email"
                value={props?.userData?.user?.email}
                disabled
              />
            </Grid>
            {/* New password input */}
            <Grid item xs={12}>
              <OutlinedInput
                fullWidth
                placeholder="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Grid>
            {/* Confirm password input */}
            <Grid item xs={12}>
              <OutlinedInput
                fullWidth
                placeholder="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Grid>
          </Grid>
          {/* Display any error messages */}
          {error.length > 0 && (
            <Typography
              color="error"
              align="center"
              style={{ marginTop: "10px" }}
            >
              {error.map((err, index) => (
                <div key={index}>{err}</div>
              ))}
            </Typography>
          )}
          <Button
            variant="contained"
            type="submit"
            fullWidth
            style={{ marginTop: "20px" }}
          >
            Save Password
          </Button>
        </form>
      </Paper>
      {/* Backdrop to show a loading indicator during API call */}
      <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress color="inherit" />
        <Typography variant="h6" style={{ marginLeft: "10px" }}>
          Saving...
        </Typography>
      </Backdrop>
    </Box>
  );
}

export default ChangePassword;
